import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import ScrollReveal from "../components/ScrollReveal";
import SectionLabel from "../components/SectionLabel";
import HorizontalTicker from "../components/HorizontalTicker";
import PortfolioCarousel from "../components/PortfolioCarousel";
import CursorCrosshair from "../components/CursorCrosshair";
import { SERVICES, PROJECTS, TESTIMONIALS, FULL_PROCESS } from "../lib/data";

/* ── Count-up number animation ─────────────────────────────── */
function CountUp({ to, suffix = "", decimals = 0, duration = 1800 }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(parseFloat((eased * to).toFixed(decimals)));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [to, decimals, duration]);

  return (
    <span ref={ref}>
      {decimals > 0 ? value.toFixed(decimals) : Math.floor(value)}{suffix}
    </span>
  );
}
/* ── Word-by-word gray → black typewriter reveal ─────────────── */
function TypewriterReveal({ text }) {
  const words = text.split(" ");
  const [revealed, setRevealed] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let i = 0;
          const interval = setInterval(() => {
            i++;
            setRevealed(i);
            if (i >= words.length) clearInterval(interval);
          }, 60); // 60ms per word
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [words.length]);

  return (
    <span ref={ref}>
      {words.map((word, i) => (
        <span
          key={i}
          style={{
            color: i < revealed ? "var(--carbon-black)" : "#ccc",
            transition: "color 0.4s ease",
            marginRight: i < words.length - 1 ? "0.28em" : 0,
            display: "inline-block",
          }}
        >
          {word}
        </span>
      ))}
    </span>
  );
}


const ACCENT_GRADIENTS = [
  "linear-gradient(135deg, #6366f1, #8b5cf6)",
  "linear-gradient(135deg, #0ea5e9, #6366f1)",
  "linear-gradient(135deg, #f59e0b, #ef4444)",
  "linear-gradient(135deg, #10b981, #0ea5e9)",
  "linear-gradient(135deg, #f43f5e, #ec4899)",
  "linear-gradient(135deg, #8b5cf6, #f43f5e)",
  "linear-gradient(135deg, #f59e0b, #10b981)",
];

const BRAND_NAMES = [
  "Lumen Coffee", "FinTrack Labs", "Verdant Naturals", "Northsail Yachts",
  "Kettle & Co.", "Atlas SaaS", "BrandLab", "Lumen Coffee", "FinTrack Labs",
  "Verdant Naturals", "Northsail Yachts", "Kettle & Co.", "Atlas SaaS", "BrandLab",
];

const CARD_GRADIENTS = [
  "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
  "linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)",
  "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
  "linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)",
  "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
  "linear-gradient(135deg, #8b5cf6 0%, #f43f5e 100%)",
  "linear-gradient(135deg, #f59e0b 0%, #10b981 100%)",
];

function ClientsSection() {
  const trackRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const total = TESTIMONIALS.length;

  // Sync dot indicator with native scroll position
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = track;
      const cardWidth = scrollWidth / total;
      const idx = Math.round(scrollLeft / cardWidth);
      setActiveIdx(Math.min(idx, total - 1));
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, [total]);

  const scrollTo = (idx) => {
    const track = trackRef.current;
    if (!track) return;
    const cardWidth = track.scrollWidth / total;
    track.scrollTo({ left: cardWidth * idx, behavior: "smooth" });
  };

  const prev = () => scrollTo(Math.max(0, activeIdx - 1));
  const next = () => scrollTo(Math.min(total - 1, activeIdx + 1));

  return (
    <section className="clients-section-v2" data-testid="testimonials-section">
      {/* ── TOP: heading + stats row ── */}
      <div className="container-x">
        <ScrollReveal>
          <SectionLabel num={6}>Clients</SectionLabel>
          <div className="cv2-head-row">
            <div className="cv2-head-left">
              <h2 className="sec-title">What Clients Say.</h2>
              <p className="cv2-sub">
                Real words from real collaborators — founders, CEOs, and creators
                who trusted us with their vision.
              </p>
            </div>
            <div className="cv2-stats-row">
              {[
                { num: "30+", lbl: "Happy Clients" },
                { num: "100%", lbl: "Satisfaction" },
                { num: "5.0 ★", lbl: "Avg. Rating" },
                { num: "5+", lbl: "Countries" },
              ].map((s, i) => (
                <div key={i} className="cv2-stat">
                  <span className="cv2-stat-num">{s.num}</span>
                  <span className="cv2-stat-lbl">{s.lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>

      {/* ── Brand ticker ── */}
      <div className="cv2-brand-strip" aria-hidden="true">
        <div className="cv2-brand-track">
          {[...BRAND_NAMES, ...BRAND_NAMES].map((brand, i) => (
            <span key={i} className="cv2-brand-item">
              <span className="cv2-brand-dot" />
              {brand}
            </span>
          ))}
        </div>
      </div>

      {/* ── Native scroll-snap card track ── */}
      <div className="cv2-scroll-outer">
        <div className="cv2-scroll-track" ref={trackRef}>
          {TESTIMONIALS.map((t, i) => {
            const initials = t.name.split(" ").map(n => n[0]).join("").slice(0, 2);
            return (
              <div
                key={i}
                className="cv2-card"
                data-testid={`testimonial-${i}`}
              >
                {/* Gradient top strip */}
                <div className="cv2-card-bar" style={{ background: CARD_GRADIENTS[i % CARD_GRADIENTS.length] }} />

                {/* Card body */}
                <div className="cv2-card-body">
                  {/* Large decorative quote */}
                  <div className="cv2-bigquote">"</div>

                  <p className="cv2-text">{t.text}</p>

                  {/* Stars */}
                  <div className="cv2-stars">
                    {[0,1,2,3,4].map(s => <Star key={s} size={14} fill="currentColor" />)}
                  </div>

                  {/* Divider */}
                  <div className="cv2-divider" />

                  {/* Author */}
                  <div className="cv2-author">
                    <div
                      className="cv2-avatar"
                      style={{ background: CARD_GRADIENTS[i % CARD_GRADIENTS.length] }}
                    >
                      {initials}
                    </div>
                    <div className="cv2-author-info">
                      <span className="cv2-author-name">{t.name}</span>
                      <span className="cv2-author-role">{t.role}</span>
                    </div>
                    <span className="cv2-card-idx">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                </div>
              </div>
            );
          })}
          {/* Trailing spacer so last card can centre */}
          <div className="cv2-trail-spacer" aria-hidden="true" />
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="container-x">
        <div className="cv2-controls">
          {/* Dot indicators */}
          <div className="cv2-dots">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                className={`cv2-dot${i === activeIdx ? " active" : ""}`}
                onClick={() => scrollTo(i)}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
          {/* Arrow buttons */}
          <div className="cv2-arrows">
            <button className="cv2-arrow" onClick={prev} disabled={activeIdx === 0} aria-label="Previous">
              <ChevronLeft size={18} />
            </button>
            <button className="cv2-arrow" onClick={next} disabled={activeIdx === total - 1} aria-label="Next">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

const HERO_WORDS = ["Innovating", "your", "brand", "identity."];


export default function Home() {
  const featured = PROJECTS.find((p) => p.featured) || PROJECTS[0];
  const others = PROJECTS.filter((p) => p.slug !== featured.slug).slice(0, 4);
  const heroRef = useRef(null);
  const [activeSvcIdx, setActiveSvcIdx] = useState(0);

  return (
    <div className="page" data-testid="home-page">
      {/* HERO */}
      <section className="hero" data-testid="hero" ref={heroRef}>
        <CursorCrosshair containerRef={heroRef} />
        <div className="container-x" style={{ position: 'relative', zIndex: 1 }}>
          <h1 className="hero-display">
            {HERO_WORDS.map((w, i) => (
              <span key={i} className="hero-word" style={{ animationDelay: `${i * 120}ms`, marginRight: i < HERO_WORDS.length - 1 ? "0.25em" : 0 }}>
                {w}
              </span>
            ))}
          </h1>

          <div className="hero-marquee" aria-hidden="true">
            <div className="hero-marquee-track">
              {Array.from({ length: 6 }).flatMap((_, k) => [
                <span key={`a-${k}`}>AI-Powered Design Studio</span>,
                <span key={`b-${k}`}>Brand Identity</span>,
                <span key={`c-${k}`}>UI/UX Design</span>,
                <span key={`d-${k}`}>Digital Content</span>,
                <span key={`e-${k}`}>Mehsana</span>,
                <span key={`f-${k}`}>Sinix Studio®</span>,
              ])}
            </div>
          </div>

          <ScrollReveal delay={500}>
            <p className="hero-paragraph">
              AI-powered design that builds brands people remember —
              by the team at <strong style={{ color: "var(--carbon-black)" }}>Sinix Studio</strong>, Mehsana.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={650}>
            <div className="hero-ctas">
              <Link to="/work" className="btn btn-primary" data-testid="hero-cta-work">
                See Our Work <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn btn-outline" data-testid="hero-cta-contact">
                Get In Touch <ArrowUpRight size={16} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* PORTFOLIO CAROUSEL */}
      <PortfolioCarousel />

      {/* SERVICES STRIP */}
      <div className="services-strip" data-testid="services-strip">
        <div className="container-x">
          <div className="services-strip-inner">
            {SERVICES.map((s) => (
              <span key={s.slug}>{s.title}</span>
            ))}
          </div>
        </div>
      </div>

      {/* SERVICES */}
      <section className="section section-bright" data-testid="services-section">
        <div className="container-x">
          <ScrollReveal>
            <SectionLabel num={1}>Services</SectionLabel>
            <h2 className="sec-title">Sinix Studio Creative Services.</h2>
            <p className="sec-sub">
              Two flagship practices, three precision add-ons. Every project driven by our team —
              shipped fast, supercharged with AI.
            </p>
          </ScrollReveal>

          {/* Desktop: two-column panel layout */}
          <div className="home-interactive-services home-interactive-services--desktop" style={{ marginTop: 64 }}>
            <div className="interactive-svc-left">
              {SERVICES.map((s, i) => (
                <div 
                  key={s.slug} 
                  className={`interactive-svc-item ${i === activeSvcIdx ? "active" : ""}`}
                  onMouseEnter={() => setActiveSvcIdx(i)}
                  onClick={() => setActiveSvcIdx(i)}
                >
                  <span className="svc-num">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="svc-name">{s.title}</h3>
                </div>
              ))}
            </div>
            
            <div className="interactive-svc-right">
              {SERVICES[activeSvcIdx] && (
                <div className="interactive-svc-content animate-fade-in" key={activeSvcIdx}>
                  <div className="svc-pills">
                    {SERVICES[activeSvcIdx].sublist.map(sub => (
                      <span key={sub} className="svc-pill">{sub}</span>
                    ))}
                  </div>
                  <p className="svc-desc">{SERVICES[activeSvcIdx].overview.what}</p>
                  <Link to={`/services/${SERVICES[activeSvcIdx].slug}`} className="svc-learn-more">
                    Learn more <ArrowRight size={18} />
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Mobile: accordion — tap service to reveal its info inline */}
          <div className="home-interactive-services--mobile" style={{ marginTop: 48 }}>
            {SERVICES.map((s, i) => {
              const isOpen = i === activeSvcIdx;
              return (
                <div key={s.slug} className={`svc-accordion-item ${isOpen ? "open" : ""}`}>
                  <button
                    className="svc-accordion-trigger"
                    onClick={() => setActiveSvcIdx(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                  >
                    <span className="svc-accordion-num">{String(i + 1).padStart(2, "0")}</span>
                    <span className="svc-accordion-title">{s.title}</span>
                    <span className="svc-accordion-chevron" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </button>
                  <div className="svc-accordion-body" aria-hidden={!isOpen}>
                    <div className="svc-accordion-inner">
                      <div className="svc-pills">
                        {s.sublist.map(sub => (
                          <span key={sub} className="svc-pill">{sub}</span>
                        ))}
                      </div>
                      <p className="svc-desc">{s.overview.what}</p>
                      <Link to={`/services/${s.slug}`} className="svc-learn-more">
                        Learn more <ArrowRight size={18} />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED WORK */}
      <section className="section section-platinum" data-testid="work-section">
        <div className="container-x">
          <ScrollReveal>
            <SectionLabel num={2}>Featured Work</SectionLabel>
            <h2 className="sec-title">Selected Projects.</h2>
          </ScrollReveal>

          {/* 1st Project - Big Hero Card (desktop only, hidden on mobile via CSS) */}
          {PROJECTS.length > 0 && (
            <ScrollReveal>
              <Link
                to={`/work/${PROJECTS[0].slug}`}
                className="ddwork-card home-hero-card"
                data-testid={`proj-card-hero-${PROJECTS[0].slug}`}
              >
                <div className="ddwork-img home-hero-img">
                  <img src={PROJECTS[0].cover} alt={PROJECTS[0].name} loading="lazy" />
                  <div className="ddwork-hover-panel">
                    <div className="ddwork-hover-content">
                      <p className="ddwork-hover-tags">{PROJECTS[0].tags.join(" / ")}</p>
                      <p className="ddwork-hover-short">{PROJECTS[0].short}</p>
                      <span className="ddwork-view-btn">View Project →</span>
                    </div>
                  </div>
                </div>
                <div className="ddwork-card-info">
                  <span className="ddwork-num">01</span>
                  <div className="ddwork-card-text">
                    <h3 className="ddwork-card-name">{PROJECTS[0].name}</h3>
                  </div>
                </div>
              </Link>
            </ScrollReveal>
          )}
        </div>

        {/* Horizontal scroll strip:
            Mobile (<768px): ALL projects shown here (home-hcard--first = project 01)
            Desktop (≥768px): only projects 02+ shown here (project 01 is the big hero card above) */}
        {PROJECTS.length > 0 && (
          <div className="home-hscroll-wrap">
            <div className="home-hscroll-track">
              {PROJECTS.map((p, i) => (
                <Link
                  key={p.slug}
                  to={`/work/${p.slug}`}
                  className={`home-hcard ddwork-card${i === 0 ? " home-hcard--first" : ""}`}
                  data-testid={`proj-card-strip-${p.slug}`}
                >
                  <div className="ddwork-img home-hcard-img">
                    <img src={p.cover} alt={p.name} loading="lazy" />
                    <div className="ddwork-hover-panel">
                      <div className="ddwork-hover-content">
                        <p className="ddwork-hover-tags">{p.tags.join(" / ")}</p>
                        <p className="ddwork-hover-short">{p.short}</p>
                        <span className="ddwork-view-btn">View Project →</span>
                      </div>
                    </div>
                  </div>
                  <div className="ddwork-card-info">
                    <span className="ddwork-num">{String(i + 1).padStart(2, "0")}</span>
                    <div className="ddwork-card-text">
                      <h3 className="ddwork-card-name">{p.name}</h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="container-x">
          <div style={{ marginTop: 40 }}>
            <Link to="/work" className="btn btn-primary" data-testid="view-all-work">
              View All Work <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section section-bright" data-testid="about-section">
        <div className="container-x">
          <ScrollReveal>
            <SectionLabel num={3}>About</SectionLabel>
          </ScrollReveal>
          <div className="about-grid">
            <ScrollReveal>
              <h2 className="sec-title">Creative. Focused. AI-Powered.</h2>
              <p className="sec-sub" style={{ marginTop: 24 }}>
                Sinix Studio is a creative team based in Mehsana — with{" "}
                <strong style={{ color: "var(--carbon-black)" }}>4+ years of real client work</strong>, combining design craft with AI-powered tooling to ship faster, smarter and better.
                Every project is driven end-to-end by our team. No diluted vision, no missed deadlines.
              </p>
              <Link to="/about" className="learn-link" style={{ marginTop: 24, display: "inline-flex" }} data-testid="about-learn">
                Learn More <ArrowRight size={14} />
              </Link>
            </ScrollReveal>

            <ScrollReveal delay={150}>
              <div className="stats-2x2" data-testid="home-stats">
                <div className="stat-mini"><div className="num"><HorizontalTicker value="3+" /></div><div className="lbl">Years Exp.</div></div>
                <div className="stat-mini"><div className="num"><HorizontalTicker value="10+" /></div><div className="lbl">Projects</div></div>
                <div className="stat-mini"><div className="num"><HorizontalTicker value="100%" /></div><div className="lbl">Satisfaction</div></div>
                <div className="stat-mini"><div className="num"><HorizontalTicker value="2×" /></div><div className="lbl">AI Speed</div></div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="section section-bright stats-section" data-testid="stats-section">
        <div className="container-x">
          <ScrollReveal>
            <SectionLabel num={4}>Stats</SectionLabel>
            <h2 className="sec-title">By the numbers.</h2>
          </ScrollReveal>

          <div className="stats-row">

            <div className="stat-item">
              <div className="stat-head">// Clients</div>
              <div className="stat-number">
                <CountUp to={30} suffix="+" duration={1400} />
              </div>
              <div className="stat-divider" />
              <p className="stat-desc">
                building long-lasting client relationships with focused, personal design work — across India and beyond
              </p>
            </div>

            <div className="stat-item">
              <div className="stat-head">// Services</div>
              <div className="stat-number">
                <CountUp to={5} suffix="" duration={1000} />
              </div>
              <div className="stat-divider" />
              <p className="stat-desc">
                specialized design services delivered with AI-powered precision — brand, UI/UX, content, video, photo
              </p>
            </div>

            <div className="stat-item">
              <div className="stat-head">// Experience</div>
              <div className="stat-number">
                <CountUp to={4} suffix="+" duration={1200} />
              </div>
              <div className="stat-divider" />
              <p className="stat-desc">
                years of real client experience across branding, UI/UX and content design — every project shipped, none abandoned
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* STATEMENT BANNER */}
      <section className="statement-banner">
        <div className="container-x">
          <p className="statement-text">
            <TypewriterReveal text="SINIX STUDIO® – Building Bold Brands Through Modern Visual Storytelling, Smooth Interactions, and Premium Web Experiences." />
          </p>
        </div>
      </section>


      {/* CLIENTS / TESTIMONIALS */}
      <ClientsSection />

      {/* STORY SECTION */}
      <section className="section section-bright" data-testid="story-section">
        <div className="container-x">
          <ScrollReveal>
            <SectionLabel num={7}>The Person Behind It</SectionLabel>
            <h2 className="sec-title">Shubham Prajapati.</h2>
            <p className="sec-sub">From a curious student in Gandhinagar to a creative founder in Mehsana — here's the short version.</p>
          </ScrollReveal>

          <div className="story-hero-row">
            <ScrollReveal>
              <div className="story-photo-wrap">
                <img src="/shubham.jpg" alt="Shubham Prajapati" />
              </div>
              <div className="story-stats-row" style={{ marginTop: 16, gridTemplateColumns: "repeat(2, 1fr)" }}>
                <div className="story-stat" style={{ padding: "16px 12px" }}><div className="story-stat-num"><HorizontalTicker value="4+" /></div><div className="story-stat-lbl">Years Exp.</div></div>
                <div className="story-stat" style={{ padding: "16px 12px" }}><div className="story-stat-num"><HorizontalTicker value="30+" /></div><div className="story-stat-lbl">Clients</div></div>
                <div className="story-stat" style={{ padding: "16px 12px" }}><div className="story-stat-num"><HorizontalTicker value="5+" /></div><div className="story-stat-lbl">Countries</div></div>
                <div className="story-stat" style={{ padding: "16px 12px" }}><div className="story-stat-num"><HorizontalTicker value="2" /></div><div className="story-stat-lbl">Studios Built</div></div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={150}>
              <div className="story-timeline" style={{ marginTop: 0 }}>

                <div className="story-timeline-item">
                  <div className="story-timeline-dot">🚀</div>
                  <div className="story-timeline-content">
                    <div className="story-tag">First Venture · 2020 – 2025</div>
                    <h4 className="story-title">EditX Studio</h4>
                    <p className="story-body">5 years · 30+ clients · Web, Branding, UI/UX & Creative Media. Partnership challenges led to closure in 2025 — building the experience and foundations that Sinix Studio is built on today.</p>
                  </div>
                </div>
                <div className="story-timeline-item highlight">
                  <div className="story-timeline-dot">🌟</div>
                  <div className="story-timeline-content">
                    <div className="story-tag">Now</div>
                    <h4 className="story-title">Sinix Studio</h4>
                    <p className="story-body">After industry work at Tech Mahindra & Devstree IT — Sinix Studio is the refined version. AI-powered, team-driven, and fully accountable.</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>

        <div className="rainbow-marquee" aria-hidden="true">
          <div className="rainbow-marquee-track">
            {Array.from({ length: 8 }).map((_, k) => (
              <span key={k}>Dont worry we use colors for your design</span>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <div className="container-x">
        <ScrollReveal>
          <div className="cta-dark" data-testid="home-cta-dark">
            <div className="lead">Let's talk</div>
            <h2>Starting a new project or want to work together?</h2>
            <div className="email">sinixstudio.in@gmail.com</div>
            <Link to="/contact" className="btn btn-light" data-testid="home-cta-button">Get In Touch <ArrowRight size={16} /></Link>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
}
